import { useCallback, useEffect, useRef } from 'react';
import { FormikProps } from 'formik';
import { useOutletContext, useParams } from 'react-router-dom';
import useDeclarations from 'hooks/useDeclarations';
import useFormUtils from 'hooks/useFormUtils';
import useProducts from 'hooks/useProducts';
import FormTabLayout from './DeclarationFormTabLayout';
import { ListPayload } from 'core/http/response';
import { ProductsTypes } from 'store/products/reducer';
import { PaginatedParams } from 'core/http/pagination';
import { hasIcs2MasterItems } from 'views/declarations/eu/ics2/utils';

type Props = {};

const DeclarationFormWrapper = (props: Props) => {
    const {
        formik,
        productsFormik,
        cancelProducts,
        clearCancel,
        houseConsignmentFormik,
        saveOpenProductIfNeeded,
        revalidateItems,
    } = useDeclarationForm() ?? {};

    const { declarationId } = useParams<{ declarationId: string }>();
    const { declaration } = useDeclarations();
    const { listDeclarationProducts, products } = useProducts();
    const { countryLowerCase: country, formType, internalType } = useFormUtils();

    const activeProductParamsRef = useRef<Partial<PaginatedParams> | undefined>(undefined);

    const handleActiveProductParamsChange = useCallback((params: Partial<PaginatedParams> | undefined) => {
        activeProductParamsRef.current = params;
    }, []);

    /**
     * Keeps the items the tabs and the item navigation work off in step with the declaration: every
     * save and refetch hands back a new declaration, and the items are reread for it. GVMS and PBN
     * have no items at all, and an F50 keeps every goods item on a house consignment - the products
     * endpoints default to the master level, which an F50 has no type for and the backend rejects.
     */
    useEffect(() => {
        if (declaration?.gvmsDeclaration || declaration?.preBoardingNotification) return;
        if (declaration?.ics2ENSDeclaration && !hasIcs2MasterItems(declaration)) return;

        if (declarationId && declaration?.id === declarationId) {
            listDeclarationProducts({
                declarationId,
                country,
                formType,
                internalType,
                params: activeProductParamsRef.current,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [declaration, declarationId]);

    if (!declaration) return null;
    return (
        <FormTabLayout
            products={products as ListPayload<ProductsTypes>}
            formik={formik}
            declaration={declaration}
            productsFormik={productsFormik!}
            cancelProducts={cancelProducts!}
            clearCancel={clearCancel}
            houseConsignmentFormik={houseConsignmentFormik}
            saveOpenProductIfNeeded={saveOpenProductIfNeeded}
            revalidateItems={revalidateItems}
            onActiveProductParamsChange={handleActiveProductParamsChange}
        />
    );
};

export default DeclarationFormWrapper;

export function useDeclarationForm() {
    return useOutletContext<{
        formik: FormikProps<any>;
        productsFormik?: FormikProps<any>;
        houseConsignmentFormik?: FormikProps<any>;
        cancelProducts?: boolean;
        clearCancel: Function;
        saveOpenProductIfNeeded?: () => Promise<boolean>;
        revalidateItems?: () => Promise<void>;
    }>();
}
